import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import useBoardStore from '../../../store/boardStore';
import { trackAnalytics } from '@dotstorming/analytics';
import { dotvotingApi, type InvitationRateLimitState } from '../../../services/dotvotingApi';

type ShareTab = 'url' | 'email';

type ShareDialogProps = {
  open: boolean;
  onClose: () => void;
  onLoadQrCode: (boardId: string) => Promise<string | null>;
  onSendInvitations: (
    emailAddresses: string,
    emailMessage: string
  ) => Promise<{
    sent: boolean;
    rateLimit?: InvitationRateLimitState;
    code?: string;
    message?: string;
  }>;
};

const ShareDialog = ({ open, onClose, onLoadQrCode, onSendInvitations }: ShareDialogProps) => {
  const boardId = useBoardStore((state) => state.boardId);
  const activeBoardId = boardId ?? '';
  const shareUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/b/${activeBoardId}` : '';

  const [tab, setTab] = useState<ShareTab>('url');
  const [emails, setEmails] = useState('');
  const [message, setMessage] = useState('');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [qrCodeError, setQrCodeError] = useState<string | null>(null);
  const [isLoadingQrCode, setIsLoadingQrCode] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [rateLimit, setRateLimit] = useState<InvitationRateLimitState | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [verificationNotice, setVerificationNotice] = useState('');
  const embedCode = `<iframe width="850" height="615" src="${shareUrl}" frameborder="0" allowfullscreen></iframe>`;

  useEffect(() => {
    if (!open) {
      return;
    }

    setTab('url');
    setEmails('');
    setMessage('');
    setCopySuccess(null);
    setCopyError(null);
    setQrCodeDataUrl(null);
    setQrCodeError(null);
    setIsLoadingQrCode(false);
    setSendSuccess(null);
    setSendError(null);
    setIsSending(false);
    setRateLimit(null);
    setEmailVerified(null);
    void dotvotingApi.getSharingVerification()
      .then(({ user }) => setEmailVerified(user.emailVerified !== false))
      .catch(() => setVerificationNotice('Unable to check email verification. Try opening Share again.'));
    void dotvotingApi
      .getInvitationRateLimit()
      .then(setRateLimit)
      .catch(() => undefined);
  }, [open]);

  const shareEnabled = useMemo(() => shareUrl.trim().length > 0, [shareUrl]);

  useEffect(() => {
    if (!open || tab !== 'url' || !activeBoardId.trim()) {
      return;
    }

    if (qrCodeDataUrl || isLoadingQrCode) {
      return;
    }

    setIsLoadingQrCode(true);
    setQrCodeError(null);
    void (async () => {
      try {
        const loadedQrCodeDataUrl = await onLoadQrCode(activeBoardId);
        if (loadedQrCodeDataUrl?.trim()) {
          setQrCodeDataUrl(loadedQrCodeDataUrl);
        } else {
          setQrCodeError('Unable to load QR code.');
        }
      } catch {
        setQrCodeError('Unable to load QR code.');
      } finally {
        setIsLoadingQrCode(false);
      }
    })();
  }, [activeBoardId, isLoadingQrCode, onLoadQrCode, open, qrCodeDataUrl, tab]);

  const handleCopyLink = async () => {
    setCopySuccess(null);
    setCopyError(null);

    if (!shareEnabled) {
      setCopyError('Unable to build share link.');
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopySuccess('Link copied');
        trackAnalytics('board_shared', { share_method: 'link', access_type: 'board' });
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (copied) {
        setCopySuccess('Link copied');
        trackAnalytics('board_shared', { share_method: 'link', access_type: 'board' });
      } else {
        setCopyError('Sorry, your browser does not support automatic copying.');
      }
    } catch {
      setCopyError('Sorry, your browser does not support automatic copying.');
    }
  };

  const handleCopyEmbedCode = async () => {
    setCopySuccess(null);
    setCopyError(null);

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(embedCode);
        setCopySuccess('Code copied');
        trackAnalytics('board_shared', { share_method: 'embed', access_type: 'board' });
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = embedCode;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (copied) {
        setCopySuccess('Code copied');
        trackAnalytics('board_shared', { share_method: 'embed', access_type: 'board' });
      } else {
        setCopyError('Sorry, your browser does not support automatic copying.');
      }
    } catch {
      setCopyError('Sorry, your browser does not support automatic copying.');
    }
  };

  const handleSendInvites = async () => {
    if (isSending) {
      return;
    }

    const trimmedEmails = emails.trim();
    if (!trimmedEmails) {
      setSendError('Please enter at least one email address.');
      return;
    }

    setIsSending(true);
    setSendSuccess(null);
    setSendError(null);

    try {
      const result = await onSendInvitations(trimmedEmails, message);
      if (result.sent) {
        if (result.rateLimit) {
          setRateLimit(result.rateLimit);
        }
        setSendSuccess('Invitations sent');
        trackAnalytics('board_shared', { share_method: 'email', access_type: 'board' });
        setEmails('');
        setMessage('');
      } else {
        if (result.code === 'INVITATION_RATE_LIMITED' || result.code === 'OUTBOUND_EMAIL_BLOCKED') {
          void dotvotingApi
            .getInvitationRateLimit()
            .then(setRateLimit)
            .catch(() => undefined);
        }
        setSendError(result.message ?? 'Unable to send invitations. Please try again.');
      }
    } finally {
      setIsSending(false);
    }
  };

  if (open && emailVerified !== true) {
    return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Verify your email to share</DialogTitle>
      <DialogContent>
        {emailVerified === false ? <Alert severity="warning">Only verified email adresses may share</Alert> : <Typography>{verificationNotice || 'Checking email verification...'}</Typography>}
        {emailVerified === false && <Typography sx={{ mt: 2 }}>Check your inbox for a verification link sent to your email address.</Typography>}
        {verificationNotice && emailVerified === false && <Typography>{verificationNotice}</Typography>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button>
        {emailVerified === false && <Button onClick={() => { void dotvotingApi.requestEmailVerification()
          .then(() => setVerificationNotice('Verification email sent.'))
          .catch((error) => setVerificationNotice(error instanceof Error ? error.message : 'Unable to send verification email.')); }}>Resend email</Button>}
      </DialogActions>
    </Dialog>;
  }

  return (
    <Dialog open={open} onClose={() => !isSending && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, nextTab: ShareTab) => setTab(nextTab)}>
          <Tab value="url" label="Share by link" sx={{ py: 2.5, px: 3 }} />
          <Tab value="email" label="Share by email" sx={{ py: 2.5, px: 3 }} />
        </Tabs>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {tab === 'url' ? (
            <Stack spacing={1.5}>
              {qrCodeError ? <Alert severity="error">{qrCodeError}</Alert> : null}
              {copySuccess ? <Alert severity="success">{copySuccess}</Alert> : null}
              {copyError ? <Alert severity="error">{copyError}</Alert> : null}
              <Typography variant="body2">Share this board using the link below.</Typography>
              <Stack spacing={1} direction="row" alignItems="center">
                <TextField value={shareUrl} fullWidth InputProps={{ readOnly: true }} />
                <IconButton
                  onClick={() => void handleCopyLink()}
                  disabled={!shareEnabled}
                  title="Copy link"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Stack>
              <Typography variant="body2">or embed it using the following code:</Typography>
              <Stack spacing={1} direction="row" alignItems="center">
                <TextField value={embedCode} fullWidth InputProps={{ readOnly: true }} />
                <IconButton
                  onClick={() => void handleCopyEmbedCode()}
                  disabled={!shareEnabled}
                  title="Copy code"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Stack>
              {isLoadingQrCode ? <Typography variant="body2">Loading QR code...</Typography> : null}
              {qrCodeDataUrl ? (
                <Box display="flex" justifyContent="center">
                  <Box
                    component="img"
                    src={qrCodeDataUrl}
                    alt="Board QR code"
                    sx={{ width: 240, maxWidth: '100%' }}
                  />
                </Box>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {rateLimit?.emailSendingBlocked ? (
                <Alert severity="error">Email sending has been disabled for this account.</Alert>
              ) : rateLimit?.remaining === 0 ? (
                <Alert severity="warning">
                  You have reached the limit of {rateLimit.limit} email invitations per hour.
                  {rateLimit.resetAt
                    ? ` You can send more after ${new Date(
                        rateLimit.resetAt
                      ).toLocaleTimeString()}.`
                    : ''}
                </Alert>
              ) : null}
              {sendSuccess ? <Alert severity="success">{sendSuccess}</Alert> : null}
              {sendError ? <Alert severity="error">{sendError}</Alert> : null}
              <TextField
                label="Email addresses"
                placeholder="name@example.com, name2@example.com"
                value={emails}
                onChange={(event) => setEmails(event.target.value)}
                disabled={isSending || rateLimit?.remaining === 0}
                fullWidth
              />
              <TextField
                label="Message (optional)"
                multiline
                minRows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                disabled={isSending || rateLimit?.remaining === 0}
                fullWidth
              />
            </Stack>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>
          Close
        </Button>
        {tab === 'email' && (
          <Button
            variant="contained"
            onClick={() => void handleSendInvites()}
            disabled={isSending || rateLimit?.remaining === 0}
          >
            Send invitations
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
